import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { isWallTime } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalTrainingSlot } from '../../db/local.js';
import {
  addExtraDay,
  cancelOccurrence,
  deleteSlot,
  listSlots,
  moveOccurrence,
  removeExtraDay,
  restoreOccurrence,
  saveSlot,
  upcomingTraining,
} from '../../db/schedule.js';
import type { UpcomingTraining } from '../../db/schedule.js';
import { readProfile, updateTrainingReminder } from '../../db/profile.js';
import { useTimeZone, useToday } from '../../hooks/useToday.js';
import { formatDay } from '../../format.js';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/**
 * Trainingstage: die nächsten Termine, darunter die Wochenregel.
 *
 * Festgelegt wird, **wann** trainiert wird — welcher Plan-Tag drankommt,
 * rotiert (`rotatePlanDays`). Einzelne Termine lassen sich verschieben oder
 * absagen, ohne die Regel anzufassen.
 */
export function TrainingDays(): React.JSX.Element {
  const { t } = useTranslation();
  const timeZone = useTimeZone();
  const [editing, setEditing] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const upcoming = useLiveQuery(
    () => (timeZone ? upcomingTraining(timeZone, 14, db) : Promise.resolve([])),
    [timeZone],
    [] as UpcomingTraining[],
  );
  const slots = useLiveQuery(() => listSlots(db), [], [] as LocalTrainingSlot[]);

  const visible = showAll ? upcoming : upcoming.slice(0, 4);

  return (
    <section aria-labelledby="training-days-heading" className="kw-tile flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h3 id="training-days-heading" className="kw-kicker">
          {t('schedule.heading')}
        </h3>
        <button
          type="button"
          aria-expanded={editing}
          onClick={() => setEditing(!editing)}
          className="kw-btn kw-btn-quiet kw-btn-small px-3"
        >
          {editing ? t('schedule.done') : t('schedule.setup')}
        </button>
      </div>

      {editing ? <SlotEditor slots={slots} /> : null}

      {slots.length === 0 && upcoming.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('schedule.empty')}</p>
      ) : upcoming.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('schedule.noneSoon')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line">
          {visible.map((entry) => (
            <OccurrenceRow
              key={`${entry.slotId ?? entry.dayId}-${entry.originDate ?? entry.date}`}
              entry={entry}
            />
          ))}
        </ul>
      )}
      {upcoming.length > 4 ? (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          aria-expanded={showAll}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {showAll ? t('schedule.fewer') : t('schedule.more', { count: upcoming.length - 4 })}
        </button>
      ) : null}
      <ExtraDay />
      <TrainingReminder />
    </section>
  );
}

function OccurrenceRow({ entry }: { entry: UpcomingTraining }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(entry.date);
  const [time, setTime] = useState(entry.time);
  const cancelled = entry.status === 'cancelled';
  const done = entry.sessionId !== null;
  const dateId = useId();
  const timeId = useId();

  return (
    <li className="flex flex-col gap-2 py-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex min-h-11 w-full items-center gap-3 text-left"
      >
        <span className={`min-w-0 flex-1 ${cancelled ? 'text-ink-muted line-through' : ''}`}>
          <span className="block text-sm font-semibold">
            {formatDay(entry.date, 'short', i18n.language)} · {entry.time}
          </span>
          <span className="block truncate text-xs text-ink-muted">
            {entry.planDayName ?? t('schedule.noPlanDay')}
            {entry.slotId === null ? ` · ${t('schedule.extra')}` : ''}
          </span>
        </span>
        {done ? (
          <span className="kw-chip shrink-0 border-good text-good">{t('schedule.trained')}</span>
        ) : cancelled ? (
          <span className="kw-chip shrink-0 border-warn text-warn">{t('schedule.cancelled')}</span>
        ) : entry.missed ? (
          <span className="kw-chip shrink-0 border-warn text-warn">{t('schedule.missed')}</span>
        ) : null}
      </button>

      {open ? (
        <div className="flex flex-col gap-2 rounded-xl border border-line bg-surface-raised p-3">
          {!cancelled ? (
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                if (!isWallTime(time) || date === '') return;
                void moveOccurrence(entry, date, time, db).then(() => setOpen(false));
              }}
            >
              <label htmlFor={dateId} className="flex w-40 flex-col gap-1">
                <span className="kw-label">{t('schedule.date')}</span>
                <input
                  id={dateId}
                  type="date"
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                  className="kw-field"
                />
              </label>
              <label htmlFor={timeId} className="flex w-28 flex-col gap-1">
                <span className="kw-label">{t('schedule.time')}</span>
                <input
                  id={timeId}
                  type="time"
                  value={time}
                  onChange={(event) => setTime(event.target.value)}
                  className="kw-field"
                />
              </label>
              <button type="submit" className="kw-btn kw-btn-small px-3">
                {t('schedule.move')}
              </button>
            </form>
          ) : null}
          <div className="flex gap-2">
            {cancelled ? (
              <button
                type="button"
                onClick={() => void restoreOccurrence(entry, db)}
                className="kw-btn kw-btn-small flex-1"
              >
                {t('schedule.restore')}
              </button>
            ) : entry.slotId === null && entry.dayId ? (
              <button
                type="button"
                onClick={() => void removeExtraDay(entry.dayId!, db)}
                className="kw-btn kw-btn-quiet kw-btn-small flex-1"
              >
                {t('schedule.remove')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void cancelOccurrence(entry, db).then(() => setOpen(false))}
                className="kw-btn kw-btn-quiet kw-btn-small flex-1"
              >
                {t('schedule.cancel')}
              </button>
            )}
          </div>
        </div>
      ) : null}
    </li>
  );
}

/** Wochenregel und Erinnerung. */
function SlotEditor({ slots }: { slots: LocalTrainingSlot[] }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-accent bg-surface-raised p-3">
      <p className="text-sm text-ink-muted">{t('schedule.rotationWhy')}</p>
      <ul className="flex flex-col gap-2">
        {slots.map((slot) => (
          <SlotRow key={slot.id} slot={slot} />
        ))}
      </ul>
      <button
        type="button"
        onClick={() => void saveSlot({ weekday: 1, time: '18:00', durationMin: 60 }, db)}
        className="kw-btn kw-btn-small"
      >
        {t('schedule.addSlot')}
      </button>
    </div>
  );
}

/**
 * Erinnerung „Losgehen um …“ — seit 18.10.2026 immer sichtbar, nicht erst
 * hinter „Trainingstage“: ab Werk ist sie aus, und dort fand sie niemand.
 */
function TrainingReminder(): React.JSX.Element | null {
  const { t } = useTranslation();
  const profile = useLiveQuery(() => readProfile(db), []);
  if (!profile) return null;
  return (
    <div className="flex flex-col gap-2 border-t border-line pt-3">
      <p className="kw-label">{t('schedule.reminder')}</p>
      <button
        type="button"
        aria-pressed={profile.trainingReminder}
        onClick={() =>
          void updateTrainingReminder({ trainingReminder: !profile.trainingReminder }, db)
        }
        className={`kw-btn kw-btn-small ${profile.trainingReminder ? '' : 'kw-btn-quiet'}`}
      >
        {profile.trainingReminder ? t('schedule.reminderOn') : t('schedule.reminderOff')}
      </button>
      <div className="flex gap-3">
        <MinutesField
          label={t('schedule.travel')}
          value={profile.travelMin}
          onCommit={(value) => void updateTrainingReminder({ travelMin: value }, db)}
        />
        <MinutesField
          label={t('schedule.lead')}
          value={profile.leadMin}
          onCommit={(value) => void updateTrainingReminder({ leadMin: value }, db)}
        />
      </div>
      <p className="text-xs text-ink-muted">{t('schedule.reminderWhy')}</p>
    </div>
  );
}

function SlotRow({ slot }: { slot: LocalTrainingSlot }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const ids = { day: useId(), time: useId(), duration: useId() };
  const weekdayName = (day: number) =>
    // 05.10.2026 war ein Montag — von dort aus die Wochentage benennen.
    formatDay(`2026-10-${String(4 + day).padStart(2, '0')}`, 'weekdayLong', i18n.language);

  return (
    <li className="flex flex-wrap items-end gap-2">
      <label htmlFor={ids.day} className="flex min-w-32 flex-1 flex-col gap-1">
        <span className="kw-label">{t('schedule.weekday')}</span>
        <select
          id={ids.day}
          value={slot.weekday}
          onChange={(event) => void saveSlot({ ...slot, weekday: Number(event.target.value) }, db)}
          className="kw-field"
        >
          {WEEKDAYS.map((day) => (
            <option key={day} value={day}>
              {weekdayName(day)}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor={ids.time} className="flex w-28 flex-col gap-1">
        <span className="kw-label">{t('schedule.time')}</span>
        <input
          id={ids.time}
          type="time"
          defaultValue={slot.time}
          onBlur={(event) => {
            if (isWallTime(event.target.value) && event.target.value !== slot.time) {
              void saveSlot({ ...slot, time: event.target.value }, db);
            }
          }}
          className="kw-field"
        />
      </label>
      <MinutesField
        id={ids.duration}
        label={t('schedule.duration')}
        value={slot.durationMin}
        onCommit={(value) => value >= 5 && void saveSlot({ ...slot, durationMin: value }, db)}
      />
      <button
        type="button"
        onClick={() => void deleteSlot(slot.id, db)}
        aria-label={t('schedule.removeSlot', { day: weekdayName(slot.weekday), time: slot.time })}
        className="kw-btn kw-btn-quiet kw-btn-small px-3"
      >
        {t('schedule.remove')}
      </button>
    </li>
  );
}

function MinutesField({
  id,
  label,
  value,
  onCommit,
}: {
  id?: string;
  label: string;
  value: number;
  onCommit: (value: number) => void;
}): React.JSX.Element {
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  return (
    <label htmlFor={inputId} className="flex w-24 flex-col gap-1">
      <span className="kw-label">{label}</span>
      <input
        id={inputId}
        inputMode="numeric"
        defaultValue={value}
        onBlur={(event) => {
          const parsed = Number(event.target.value);
          if (Number.isInteger(parsed) && parsed >= 0 && parsed <= 600 && parsed !== value) {
            onCommit(parsed);
          }
        }}
        className="kw-field"
      />
    </label>
  );
}

/** Ein zusätzlicher Termin außerhalb der Regel. */
function ExtraDay(): React.JSX.Element {
  const { t } = useTranslation();
  const today = useToday();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('18:00');
  const ids = { date: useId(), time: useId() };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setDate(today ?? '');
          setOpen(true);
        }}
        className="kw-btn kw-btn-quiet kw-btn-small"
      >
        {t('schedule.addExtra')}
      </button>
    );
  }
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (!isWallTime(time) || date === '') return;
        void addExtraDay({ date, time, durationMin: 60 }, db).then(() => setOpen(false));
      }}
    >
      <label htmlFor={ids.date} className="flex w-40 flex-col gap-1">
        <span className="kw-label">{t('schedule.date')}</span>
        <input
          id={ids.date}
          type="date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
          className="kw-field"
        />
      </label>
      <label htmlFor={ids.time} className="flex w-28 flex-col gap-1">
        <span className="kw-label">{t('schedule.time')}</span>
        <input
          id={ids.time}
          type="time"
          value={time}
          onChange={(event) => setTime(event.target.value)}
          className="kw-field"
        />
      </label>
      <button type="submit" className="kw-btn kw-btn-small px-3">
        {t('schedule.add')}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="kw-btn kw-btn-quiet kw-btn-small px-3"
      >
        {t('nutrition.library.cancel')}
      </button>
    </form>
  );
}
